import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiImageSize, FileType, ObjectTypes } from '@fi-shared/types';
import { LinkToModel } from '@fi-shared/link-to/link-to.model';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval, Observable, Subject } from 'rxjs';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { isDefined } from '@fi-shared/utils';
import { FiVimeoPreviewSize } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-player.component';
import { TextItemMediaClickActionType } from '@fi-shared/types/text-item-media-click-action-type';
import { FiDocumentTemplateOptionsService } from '@fi-app/document-template/document-template-options.service';
import { FiDisplayAs } from '@fi-shared/types/display-as';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { TemplatePlaceHolderControlModel } from '@fi-app/template/template-placeholder-control.model';
import { TemplateLayoutHelper } from '@fi-app/template/template-layout-helper';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { FiDndDetails, FiDndEventType } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-helper';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { ImageMovieFileTypes } from '@fi-shared/fi-insert-media/fi-insert-media-consts';

export interface IDocumentLayoutImageModel {
    linkTo?: LinkToModel;
    Name?: string;
    FileType: FileType;
    Url?: string; // value for backward compatibility
    Extension?: string;
    MediaBankFileId: number;
    StorageType: MediaBankStorageType;
    PhysicalFileName: string;
    ChangedOn: string;
    IsNewUpload: boolean;
    HideFromMediaBank: boolean;
    MediaSizeClass?: string;
    AlignmentClass?: string;
    DisplayAs?: number;
    OnClickActionType?: TextItemMediaClickActionType;
    LinkText?: string;
}

@Component({
    selector: 'fi-document-layout-item-image-column',
    templateUrl: './image-column.component.html',
    styleUrls: ['./image-column.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-document-layout-item-image-column' },
    standalone: false,
})
export class FiDocumentLayoutItemImageColumnComponent implements OnInit, OnChanges, OnDestroy {
    @Input() disableEdit: boolean = false;
    @Input() column: number;
    @Input() itemModel: TemplatePlaceHolderControlModel;
    @Input() contentFile: IDocumentLayoutImageModel;
    @Input() mediaBankFileId: number;
    @Input() templateLayoutHelper: TemplateLayoutHelper;

    @Output() afterRender = new EventEmitter();
    @Output() contentFileChange = new EventEmitter<IMediaBankFileReference>();
    @ViewChild('imgElement') imgElement: HTMLElement;
    @ViewChild('videoElement') videoElement: HTMLElement;
    FiVimeoPreviewSize = FiVimeoPreviewSize;
    //private column;
    openSettingsDialog$ = new Subject<void>();

    constructor(
        public elementRef: ElementRef<HTMLElement>,
        private linkToTypesService: LinkToTypesService,
        private mediaStorageService: FiMediaStorageService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private fiTextItemHelperService: FiTextItemHelperService,
        private cdr: ChangeDetectorRef,
        private fileUpload2Service: FiFileUpload2Service,
        private documentTemplateOptionsService: FiDocumentTemplateOptionsService,
        private mediaViewerService: MediaViewerService,
        private mediaBankHelperService: MediaBankHelperService,
        private stateService: FiStateService,
    ) {
        //this.column = this.textItemMediaReferencesService.getNextMediaColumnIdx();
    }

    ngOnInit() {
        if (this.contentFile?.MediaBankFileId && !this.getCurrentMediaBankFileReference()) {
            // media file was deleted but still present in content
            this.contentFileChange.emit(undefined);
            console.log('media bank file was deleted', this.contentFile?.MediaBankFileId);
        }

        if (this.contentFile && isDefined(this.contentFile.Url) && !isDefined(this.contentFile.FileType)) {
            // https://chainformation.atlassian.net/browse/FI-4814
            this.contentFileChange.emit(undefined);
            console.log('wrong media bank file', this.contentFile);
        }

        if (this.isEmpty()) this.afterRender.emit();

        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.onResize());
    }

    isDisplayAsLink(): boolean {
        return this.contentFile.DisplayAs === FiDisplayAs.DownloadableFile.Id;
    }

    isDisplayAsDocument(): boolean {
        return [FileType.Pdf, FileType.Document].includes(this.contentFile.FileType);
    }

    getMediaElement(): HTMLElement {
        return this.contentFile.StorageType === MediaBankStorageType.Vimeo ? this.videoElement : this.imgElement;
    }

    isCurrentStateTemplate(): boolean {
        return (
            this.stateService.current.name === FiStateNames.Document.Template.Add ||
            this.stateService.current.name === FiStateNames.Document.Template.Edit
        );
    }

    onMediaLinkClick(): void {
        if (!this.disableEdit) {
            if (!this.isDisplayAsLink()) {
                return;
            }

            this.openSettingsDialog$.next();
            return;
        }
        if (this.contentFile.OnClickActionType === TextItemMediaClickActionType.DownloadFile) {
            this.mediaBankHelperService.downloadMediaBankFileRef(this.getCurrentMediaBankFileReference());
        }
        if (this.contentFile.OnClickActionType === TextItemMediaClickActionType.EnlargedView) {
            this.showImagePreview();
        }
    }

    private showImagePreview(): void {
        const mediaBankFileReference = this.getCurrentMediaBankFileReference();
        const changedOn = mediaBankFileReference?.ChangedOn;
        const mediaBankFileUrl = this.mediaStorageService.getMediaFileUrlByFileName(
            mediaBankFileReference.FullName,
            FiImageSize.Large,
            changedOn,
        );
        this.mediaViewerService.Show(mediaBankFileUrl, undefined, [mediaBankFileReference]);
    }

    private getCurrentMediaBankFileReference(): IMediaBankFileReference {
        return this.templateLayoutHelper.findMediaReference(this.contentFile?.MediaBankFileId);
    }

    deleteContentFile(): void {
        this.documentTemplateOptionsService.onRemoveFileFromSelectedItem$.next(this.elementRef.nativeElement);
    }

    uploadFile(file: File): void {
        this.fileUpload2Service.silentUploadFiles([file]).then((mediaBankReferences: IMediaBankFileReference[]) => {
            this.contentFileChange.emit(mediaBankReferences[0]);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        /*const change = changes.contentFile;
        if (change) {
            const currentMediaBankFileId: number = change.currentValue && change.currentValue.MediaBankFileId ?
                change.currentValue.MediaBankFileId : null;

            if (change.firstChange) {
                // add column property
                const existingReference = this.mediaReferences ? this.mediaReferences.find(item => {
                    return item.MediaBankFileId === currentMediaBankFileId
                }) : undefined;
                if (existingReference)
                    existingReference.column = this.column

            } else {
                // remove all existing references with specified column
                const existingReferences = this.mediaReferences.filter(item => item.column === this.column);
                existingReferences.forEach(item => {
                    this.mediaReferences.splice(this.mediaReferences.indexOf(item), 1);
                });
                if (currentMediaBankFileId) {
                    this.mediaReferences.push(<any>{
                        MediaBankFileId: currentMediaBankFileId,
                        column: this.column
                    });
                }
            }
        }*/
    }

    private _showInsertFileErrorTooltip$ = new Subject<void>();
    protected get showInsertFileErrorTooltip$(): Observable<void> {
        return this._showInsertFileErrorTooltip$.asObservable();
    }
    protected onDndDetails($event: FiDndDetails) {
        if ($event.dndEventType === FiDndEventType.DragEnter && !$event.isDndAllowed) {
            this._showInsertFileErrorTooltip$.next();
        }
    }

    isEmpty() {
        return !this.contentFile || !this.contentFile.Url || !this.contentFile.Url.length;
    }

    getLinkToUrl() {
        return this.linkToTypesService.getUrlForItemLinkModel(this.contentFile.linkTo);
    }

    isVideo(): boolean {
        return this.contentFile && this.contentFile.FileType === FileType.Movie;
    }

    isImage(): boolean {
        return this.contentFile && this.contentFile.FileType === FileType.Image;
    }

    GetUrl(element?: HTMLElement) {
        if (!isDefined(element)) {
            element = this.elementRef.nativeElement.querySelector('.media-preview-container');
        }
        let size: FiImageSize = undefined;
        if (!this.isVideo()) size = this.fiTextItemHelperService.getSize(element);

        //this.mediaReferences.find(item => item.MediaBankFileId === this.mediaBankFileId);
        let contentFile = Object.assign({}, this.contentFile);
        const existingMediaBankFileReference = this.templateLayoutHelper.findMediaReference(
            this.contentFile.MediaBankFileId,
        );
        if (existingMediaBankFileReference) {
            contentFile.ChangedOn = existingMediaBankFileReference.ChangedOn;
        }

        let url = this.mediaStorageService.getMediaFileUrl(contentFile, { size: size });
        return url;
    }

    internalOnLoad() {
        this.afterRender.emit();
    }

    private onResize() {
        if (!this.isEmpty() && !this.isVideo()) {
            // https://chainformation.atlassian.net/browse/FI-3856
            // recalculate url
            this.cdr.detectChanges();
        }
    }

    isVimeoStorage() {
        return this.mediaStorageService.isVimeoStorage(this.contentFile.StorageType);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
    protected readonly ImageMovieFileTypes = ImageMovieFileTypes;
}
